import React, { useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import useTheme from '@mui/material/styles/useTheme';
import PhotoLibrary from '@mui/icons-material/PhotoLibrary';
import GetApp from '@mui/icons-material/GetApp';

import { useBookingsContext } from '../../contexts/BookingsContext';
import DetailsItem from '../../components/DetailsItem';
import Spinner from '../../components/Spinner';
import { formatMobileNumber, formatPostalCode } from '../../utils/helpers';
import { COLORS } from '../../utils/colors';
import toggleToast from '../../utils/messaging';

import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import { StyledButton, StyledDetailsHeading, StyledSubText } from './styles';
import Report from './Report';
import useSession from '../../hooks/useSession';

const ManageReport = () => {
  const theme = useTheme();

  console.log('::::ManageReport...');

  const { selectedBooking, isFetchingSessionDetails } = useBookingsContext();
  const { downloadSessionData } = useSession();

  const sessionDetails = selectedBooking;
  const imageCount = !sessionDetails?.urls ? 0 : sessionDetails?.urls?.length;

  const convertWebpToJpg = (blob: Blob): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob(
          (jpgBlob) => {
            resolve(jpgBlob as Blob);
          },
          'image/jpeg',
          0.95, // Quality
        );
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const download = (fname: string, blob: any) => {
    console.log(`download: ${fname}`);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${fname}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log(`done: ${fname}`);
  };

  const handleDownloadImages = (
    nidx: number,
    convertToJpg: boolean = false,
  ) => {
    if (nidx < imageCount) {
      downloadImageAsync(sessionDetails.urls[nidx], nidx, convertToJpg);
    }
  };

  const downloadImageAsync = async (
    url: string,
    nidx: number,
    convertToJpg: boolean,
  ) => {
    console.log(`fetch: ${url}`);
    const response = await axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
    });

    let blob = new Blob([response.data]);

    if (convertToJpg && blob.type !== 'image/jpeg') {
      console.log('Converting to jpg...');
      blob = await convertWebpToJpg(blob);
    }

    console.log(`blob size: ${blob.size}`);

    const fname = convertToJpg
      ? `vhc_${url.split('/').slice(-2).join('_')}`.replace(
          /\.(webp|png|gif)$/,
          '.jpg',
        )
      : `vhc_${url.split('/').slice(-2).join('_')}`;

    const imageUrl =
      window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(blob)
        : window.webkitURL.createObjectURL(blob);
    const link = document.createElement('a');

    link.style.display = 'none';
    link.href = imageUrl;
    link.setAttribute('download', fname);

    setTimeout(() => {
      console.log('Download completed');

      document.body.removeChild(link);
      window.URL.revokeObjectURL(imageUrl);
      console.log(`done: ${fname}`);

      handleDownloadImages(nidx + 1, convertToJpg);
    }, 250);

    document.body.appendChild(link);
    link.click();
  };

  const handleDownloadTranscript = async () => {
    try {
      const transcript =
        (await downloadSessionData?.({
          bookingId: selectedBooking.booking_id,
        })) ?? '';
      if (transcript?.length) {
        download(
          `transcript_${sessionDetails.booking_session_code}.txt`,
          new Blob([transcript], { type: 'text/plain' }),
        );
        toggleToast(
          'Transcript has been saved to your "Download" folder',
          'success',
        );
      }
    } catch (error) {
      console.log('ERROR', error);
      toggleToast('Failed to download transcript', 'error');
    }
  };

  return (
    <>
      {isFetchingSessionDetails ? (
        <Spinner open={isFetchingSessionDetails} />
      ) : (
        <Box height={`calc(100% - 0px)`}>
          <Box p="2.25rem" height={`calc(100% - 3.625rem)`} overflow="hidden">
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" flexDirection="column">
                <Typography fontSize="1.125rem" fontWeight={600}>
                  Complete session details
                </Typography>
                <StyledSubText>
                  View the complete VHC session details for the VHC call
                </StyledSubText>
              </Box>
            </Box>

            <Box display="flex" py="3rem" height="100%">
              <Box width="45%" pr="2.25rem">
                <Box mb="3rem">
                  <StyledDetailsHeading>
                    HOME OWNER DETAILS
                  </StyledDetailsHeading>

                  <Grid container alignSelf="center" ml="3rem">
                    <DetailsItem
                      heading="NAME"
                      value={selectedBooking?.title}
                    />
                    <DetailsItem
                      heading="EMAIL"
                      value={selectedBooking?.payment?.user?.email}
                    />
                    <DetailsItem
                      heading="NUMBER"
                      value={
                        selectedBooking?.mobile_number
                          ? `${formatMobileNumber(selectedBooking?.mobile_number)}`
                          : ''
                      }
                    />
                    <DetailsItem
                      heading="ADDRESS"
                      value={
                        `${selectedBooking?.address}\\\\n${selectedBooking?.city}, ${selectedBooking?.province}\\\\n${formatPostalCode(selectedBooking?.postal_code)}` ||
                        ''
                      }
                    />
                  </Grid>
                </Box>

                <Box>
                  <StyledDetailsHeading>HOME OWNER NOTES</StyledDetailsHeading>

                  <Grid container alignSelf="center" my={'0.75rem'} ml="3rem">
                    <Grid item md={12}>
                      <Typography
                        fontSize="0.875rem"
                        fontWeight={500}
                        color={COLORS.TEXT}>
                        {selectedBooking?.description.trim() || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Box
                pl="36px"
                width="55%"
                borderLeft={`1px solid ${theme.palette.divider}`}>
                <Box>
                  <StyledDetailsHeading>
                    VHC SESSION DETAILS
                  </StyledDetailsHeading>

                  <Grid container alignSelf="center" ml="3rem">
                    <DetailsItem
                      heading="HOMEPRO"
                      value={sessionDetails?.inspector?.name}
                    />
                    <DetailsItem
                      heading="HOMEPRO EMAIL"
                      value={sessionDetails?.inspector?.email}
                    />
                    <DetailsItem
                      heading="HOMEPRO NUMBER"
                      value={
                        sessionDetails?.mobile_number
                          ? `${formatMobileNumber(
                              sessionDetails?.inspector?.mobile_number,
                            )}`
                          : ''
                      }
                    />
                    <DetailsItem
                      heading="SESSION DATE"
                      value={dayjs(sessionDetails?.booking_date).format(
                        'MMMM D, YYYY h:mm A EST',
                      )}
                    />
                  </Grid>
                </Box>
                <Grid
                  container
                  alignSelf="center"
                  color={COLORS.PRIMARY}
                  my="3rem">
                  <Grid item md={6} xs={12}>
                    <StyledButton
                      disabled={!(imageCount > 0)}
                      startIcon={<PhotoLibrary />}
                      variant="outlined"
                      onClick={() => handleDownloadImages(0, false)}
                      color="inherit"
                      sx={{ mr: 1 }}>
                      {`Download ${imageCount > 0 ? `${imageCount} ` : ''}image${imageCount > 1 ? 's' : ''}`}
                    </StyledButton>

                    <StyledButton
                      disabled={!(imageCount > 0)}
                      startIcon={<PhotoLibrary />}
                      variant="outlined"
                      onClick={() => handleDownloadImages(0, true)}
                      color="inherit">
                      {`Download ${imageCount} as JPG${imageCount > 1 ? 's' : ''}`}
                    </StyledButton>
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <StyledButton
                      disabled={
                        !sessionDetails?.transcript_exists &&
                        !sessionDetails?.transcript_v2_exists
                      }
                      startIcon={<GetApp />}
                      variant="outlined"
                      color="inherit"
                      onClick={handleDownloadTranscript}>
                      Download transcript
                    </StyledButton>
                  </Grid>
                </Grid>
                {/*<Report booking={bookingDetails} />*/}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ManageReport;
